import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import Litepicker from '@/components/Base/Litepicker';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import Table from '@/components/Base/Table';
import Pagination from '@/components/Base/Pagination';
import { FormSelect } from '@/components/Base/Form';
import {
  fetchAdminLogs,
  type LogActivity,
  type LogDataEntry,
  type AdminLogsSummary,
} from '@/api/logs';

const LOGS_DATE_RANGE_KEY = 'ldsurvey:admin:logsDateRange';
const PAGE_SIZE_OPTIONS = [25, 50, 100, 250] as const;

const mytFormatter = new Intl.DateTimeFormat('en-MY', {
  timeZone: 'Asia/Kuala_Lumpur',
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function formatMYT(iso: string): string {
  try {
    return mytFormatter.format(new Date(iso));
  } catch {
    return iso;
  }
}

function todayMYT() {
  return dayjs().format('YYYY-MM-DD');
}

function todayISORange() {
  const today = dayjs().format('YYYY-MM-DD');
  return {
    startISO: dayjs(today).startOf('day').toISOString(),
    endISO: dayjs(today).endOf('day').toISOString(),
  };
}

function toIsoRange(range: string) {
  const raw = (range || '').trim();
  if (!raw) return null;
  const [startRaw, endRaw] = raw.split(' - ');
  const startStr = (startRaw || '').trim();
  const endStr = ((endRaw || '') || startStr).trim();
  if (!startStr) return null;
  const start = dayjs(startStr);
  const end = dayjs(endStr);
  if (!start.isValid() || !end.isValid()) return null;
  return {
    startISO: start.startOf('day').toISOString(),
    endISO: end.endOf('day').toISOString(),
  };
}

function statusClassName(status: LogDataEntry['status']): string {
  if (status === 'Completed') return 'bg-emerald-100 text-emerald-700';
  if (status === 'OTP verified') return 'bg-blue-100 text-blue-700';
  return 'bg-slate-100 text-slate-600';
}

function activityIcon(type: LogActivity['type']): {
  icon: 'ClipboardCheck' | 'ShieldCheck' | 'PlayCircle' | 'Gift' | 'QrCode' | 'UserPlus' | 'UserCheck';
  className: string;
} {
  switch (type) {
    case 'survey_completed':
      return { icon: 'ClipboardCheck', className: 'bg-primary/10 text-primary' };
    case 'otp_verified':
      return { icon: 'ShieldCheck', className: 'bg-blue-100 text-blue-600' };
    case 'tng_sent':
      return { icon: 'Gift', className: 'bg-amber-100 text-amber-700' };
    case 'qr_scan':
      return { icon: 'QrCode', className: 'bg-emerald-100 text-emerald-600' };
    case 'staff_registered':
      return { icon: 'UserCheck', className: 'bg-violet-100 text-violet-600' };
    case 'staff_added':
      return { icon: 'UserPlus', className: 'bg-slate-100 text-slate-600' };
    default:
      return { icon: 'PlayCircle', className: 'bg-slate-100 text-slate-600' };
  }
}

type TabKey = 'entries' | 'activities';

function Main() {
  const readInitialRange = () => {
    if (typeof window === 'undefined') return `${todayMYT()} - ${todayMYT()}`;
    return window.localStorage.getItem(LOGS_DATE_RANGE_KEY)?.trim() || `${todayMYT()} - ${todayMYT()}`;
  };

  const [dateRange, setDateRange] = useState(readInitialRange);
  const [confirmedRange, setConfirmedRange] = useState(() => toIsoRange(readInitialRange()));
  const [activeTab, setActiveTab] = useState<TabKey>('activities');
  const [dataEntries, setDataEntries] = useState<LogDataEntry[]>([]);
  const [activities, setActivities] = useState<LogActivity[]>([]);
  const [summary, setSummary] = useState<AdminLogsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activityTypeFilter, setActivityTypeFilter] = useState<'all' | LogActivity['type']>('all');
  const [entriesPage, setEntriesPage] = useState(1);
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(50);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const value = dateRange.trim();
    if (value) window.localStorage.setItem(LOGS_DATE_RANGE_KEY, value);
  }, [dateRange]);

  const loadLogs = useCallback(async () => {
    if (!confirmedRange) return;

    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdminLogs(confirmedRange);
      setDataEntries(data.dataEntries);
      setActivities(data.activities);
      setSummary(data.summary);
      setEntriesPage(1);
      setActivitiesPage(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs');
      setDataEntries([]);
      setActivities([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [confirmedRange]);

  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  const handleApplyRange = () => {
    const iso = toIsoRange(dateRange);
    if (!iso) {
      setError('Please select a valid date range');
      return;
    }
    setConfirmedRange(iso);
  };

  const searchLower = search.trim().toLowerCase();

  const filteredEntries = useMemo(() => {
    if (!searchLower) return dataEntries;
    return dataEntries.filter((row) => {
      const hay = [
        row.contact,
        row.name,
        row.state,
        row.race,
        row.staffName,
        row.staffCode,
        row.status,
        formatMYT(row.createdAt),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(searchLower);
    });
  }, [dataEntries, searchLower]);

  const filteredActivities = useMemo(() => {
    return activities.filter((row) => {
      if (activityTypeFilter !== 'all' && row.type !== activityTypeFilter) return false;
      if (!searchLower) return true;
      const hay = [
        row.label,
        row.contact,
        row.staffName,
        row.staffCode,
        row.detail,
        formatMYT(row.at),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(searchLower);
    });
  }, [activities, activityTypeFilter, searchLower]);

  const entriesTotalPages = Math.max(1, Math.ceil(filteredEntries.length / pageSize));
  const activitiesTotalPages = Math.max(1, Math.ceil(filteredActivities.length / pageSize));

  const pagedEntries = filteredEntries.slice(
    (entriesPage - 1) * pageSize,
    entriesPage * pageSize
  );
  const pagedActivities = filteredActivities.slice(
    (activitiesPage - 1) * pageSize,
    activitiesPage * pageSize
  );

  useEffect(() => {
    if (entriesPage > entriesTotalPages) setEntriesPage(entriesTotalPages);
  }, [entriesPage, entriesTotalPages]);

  useEffect(() => {
    if (activitiesPage > activitiesTotalPages) setActivitiesPage(activitiesTotalPages);
  }, [activitiesPage, activitiesTotalPages]);

  return (
    <div className="p-6 space-y-5">
      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Logs</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              All data entries and system activities
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative w-full sm:min-w-[280px]">
              <Lucide
                icon="Calendar"
                className="absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <Litepicker
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                options={{
                  format: 'YYYY-MM-DD',
                  singleMode: false,
                  numberOfColumns: 2,
                  numberOfMonths: 2,
                  autoApply: false,
                }}
                className="w-full rounded-lg border border-slate-200/80 bg-white py-2.5 pl-10 pr-4 text-sm dark:border-darkmode-400 dark:bg-darkmode-600"
              />
            </div>
            <Button type="button" variant="primary" onClick={handleApplyRange}>
              Search
            </Button>
          </div>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { label: 'Data Entries', value: summary.dataEntryCount },
            { label: 'Activities', value: summary.activityCount },
            { label: 'Completed Surveys', value: summary.completedSurveys },
            { label: 'QR Scans', value: summary.qrScans },
          ].map((item) => (
            <div
              key={item.label}
              className="box rounded-xl border border-slate-200/60 px-4 py-3 shadow-sm dark:border-darkmode-400"
            >
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {item.label}
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-800 dark:text-slate-100">
                {loading ? '…' : item.value.toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 shadow-sm overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-slate-200/80 px-5 py-4 dark:border-darkmode-400 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex rounded-lg border border-slate-200/80 p-1 dark:border-darkmode-400">
            <button
              type="button"
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'activities'
                  ? 'bg-primary text-white'
                  : 'text-slate-600 hover:text-slate-800 dark:text-slate-400'
              }`}
              onClick={() => setActiveTab('activities')}
            >
              Activities
            </button>
            <button
              type="button"
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'entries'
                  ? 'bg-primary text-white'
                  : 'text-slate-600 hover:text-slate-800 dark:text-slate-400'
              }`}
              onClick={() => setActiveTab('entries')}
            >
              Data Entries
            </button>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {activeTab === 'activities' && (
              <FormSelect
                value={activityTypeFilter}
                onChange={(e) => {
                  setActivityTypeFilter(e.target.value as 'all' | LogActivity['type']);
                  setActivitiesPage(1);
                }}
                className="sm:w-44"
              >
                <option value="all">All activity types</option>
                <option value="session_started">Session started</option>
                <option value="otp_verified">OTP verified</option>
                <option value="survey_completed">Survey completed</option>
                <option value="tng_sent">TnG sent</option>
                <option value="qr_scan">QR scan</option>
                <option value="staff_added">Ambassador added</option>
                <option value="staff_registered">Ambassador registered</option>
              </FormSelect>
            )}
            <div className="relative">
              <Lucide
                icon="Search"
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setEntriesPage(1);
                  setActivitiesPage(1);
                }}
                placeholder="Search…"
                className="w-full rounded-lg border border-slate-200/80 py-2 pl-10 pr-3 text-sm dark:border-darkmode-400 dark:bg-darkmode-600 sm:w-56"
              />
            </div>
          </div>
        </div>

        {activeTab === 'activities' ? (
          <>
            <ul className="divide-y divide-slate-100 dark:divide-darkmode-400">
              {loading ? (
                <li className="py-12 text-center text-slate-400">
                  <Lucide icon="Loader" className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  Loading activities…
                </li>
              ) : pagedActivities.length === 0 ? (
                <li className="py-12 text-center text-sm text-slate-400">No activities found</li>
              ) : (
                pagedActivities.map((item) => {
                  const meta = activityIcon(item.type);
                  return (
                    <li key={item.id} className="flex items-start gap-4 px-5 py-4">
                      <span
                        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${meta.className}`}
                      >
                        <Lucide icon={meta.icon} className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                              {item.label}
                            </p>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              {[
                                item.contact && `Contact: ${item.contact}`,
                                item.staffName && `Ambassador: ${item.staffName}`,
                                item.staffCode && `(${item.staffCode})`,
                                item.detail,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                          </div>
                          <p className="text-xs tabular-nums text-slate-400 sm:text-right sm:whitespace-nowrap">
                            {formatMYT(item.at)}
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                })
              )}
            </ul>
            {!loading && filteredActivities.length > 0 && (
              <div className="flex flex-col gap-3 border-t border-slate-200/80 px-5 py-4 dark:border-darkmode-400 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-sm text-slate-500">
                  Showing {(activitiesPage - 1) * pageSize + 1} to{' '}
                  {Math.min(activitiesPage * pageSize, filteredActivities.length)} of{' '}
                  {filteredActivities.length} activities
                </span>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <FormSelect
                    value={String(pageSize)}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setActivitiesPage(1);
                    }}
                    className="sm:w-28"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size} / page
                      </option>
                    ))}
                  </FormSelect>
                  <Pagination>
                    <Pagination.Link
                      onClick={(e) => {
                        e.preventDefault();
                        setActivitiesPage((p) => Math.max(1, p - 1));
                      }}
                    >
                      ‹
                    </Pagination.Link>
                    <Pagination.Link active>{activitiesPage}</Pagination.Link>
                    <Pagination.Link
                      onClick={(e) => {
                        e.preventDefault();
                        setActivitiesPage((p) => Math.min(activitiesTotalPages, p + 1));
                      }}
                    >
                      ›
                    </Pagination.Link>
                  </Pagination>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table className="text-sm">
                <Table.Thead>
                  <Table.Tr>
                    {[
                      'Created At',
                      'Contact',
                      'Name',
                      'Status',
                      'Submission #',
                      'State',
                      'Ambassador',
                      'Staff Code',
                      'TnG Sent',
                    ].map((label) => (
                      <Table.Th key={label} className="whitespace-nowrap px-4 py-3">
                        {label}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {loading ? (
                    <Table.Tr>
                      <Table.Td colSpan={9} className="py-12 text-center text-slate-400">
                        <Lucide icon="Loader" className="mx-auto mb-2 h-5 w-5 animate-spin" />
                        Loading data entries…
                      </Table.Td>
                    </Table.Tr>
                  ) : pagedEntries.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={9} className="py-12 text-center text-slate-400">
                        No data entries found
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    pagedEntries.map((row) => (
                      <Table.Tr key={row.uuid}>
                        <Table.Td className="whitespace-nowrap px-4 py-3 tabular-nums">
                          {formatMYT(row.createdAt)}
                        </Table.Td>
                        <Table.Td className="px-4 py-3">{row.contact || '—'}</Table.Td>
                        <Table.Td className="px-4 py-3">{row.name || '—'}</Table.Td>
                        <Table.Td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusClassName(row.status)}`}
                          >
                            {row.status}
                          </span>
                        </Table.Td>
                        <Table.Td className="px-4 py-3 tabular-nums">
                          {row.submissionIndex ?? '—'}
                        </Table.Td>
                        <Table.Td className="px-4 py-3">{row.state || '—'}</Table.Td>
                        <Table.Td className="px-4 py-3">{row.staffName || '—'}</Table.Td>
                        <Table.Td className="px-4 py-3">{row.staffCode || '—'}</Table.Td>
                        <Table.Td className="px-4 py-3">{row.tng_sent ? 'Yes' : 'No'}</Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </div>
            {!loading && filteredEntries.length > 0 && (
              <div className="flex flex-col gap-3 border-t border-slate-200/80 px-5 py-4 dark:border-darkmode-400 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-sm text-slate-500">
                  Showing {(entriesPage - 1) * pageSize + 1} to{' '}
                  {Math.min(entriesPage * pageSize, filteredEntries.length)} of{' '}
                  {filteredEntries.length} entries
                </span>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <FormSelect
                    value={String(pageSize)}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setEntriesPage(1);
                    }}
                    className="sm:w-28"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size} / page
                      </option>
                    ))}
                  </FormSelect>
                  <Pagination>
                    <Pagination.Link
                      onClick={(e) => {
                        e.preventDefault();
                        setEntriesPage((p) => Math.max(1, p - 1));
                      }}
                    >
                      ‹
                    </Pagination.Link>
                    <Pagination.Link active>{entriesPage}</Pagination.Link>
                    <Pagination.Link
                      onClick={(e) => {
                        e.preventDefault();
                        setEntriesPage((p) => Math.min(entriesTotalPages, p + 1));
                      }}
                    >
                      ›
                    </Pagination.Link>
                  </Pagination>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Main;
